import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { CircularProgress, Box, Typography } from '@mui/material';
import { AppLayout } from './components/layout/AppLayout';
import { getSurveyResultsUrl, getSurveyShareUrl } from './lib/surveyUrls';
import { isTrialSignup, requestedBillingTier, storePendingBillingTier } from './lib/billing';
import type { EmbedCapability } from '@responsedock/types';
import { useAuthStore } from './store/auth';
import { FRAMED_PLACEHOLDER_MIN_HEIGHT, fullPageMinHeight, isInFrame } from './lib/embedResize';
import { AppErrorBoundary } from './components/AppErrorBoundary';

/** Full viewport on its own page; a fixed placeholder inside a frame so the host frame can shrink. */
const placeholderMinHeight = (framed = isInFrame()) => fullPageMinHeight(framed) ?? FRAMED_PLACEHOLDER_MIN_HEIGHT;

// Pages load on first use so an embedded questionnaire does not download the
// whole product (charts, AI designer, settings) before it can render.
const lazyPage = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  React.lazy(() => load().then((module) => ({ default: module[name] })));
const LoginPage = lazyPage(() => import('./pages/LoginPage'), 'LoginPage');
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage');
const RemoveAccountPage = lazyPage(() => import('./pages/RemoveAccountPage'), 'RemoveAccountPage');
const VerifyEmailPage = lazyPage(() => import('./pages/VerifyEmailPage'), 'VerifyEmailPage');
const ConfirmEmailChangePage = lazyPage(() => import('./pages/ConfirmEmailChangePage'), 'ConfirmEmailChangePage');
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'), 'ProfilePage');
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'DashboardPage');
const SurveysPage = lazyPage(() => import('./pages/SurveysPage'), 'SurveysPage');
const SurveyBuilderPage = lazyPage(() => import('./pages/SurveyBuilderPage'), 'SurveyBuilderPage');
const SurveyAnalyticsPage = lazyPage(() => import('./pages/SurveyAnalyticsPage'), 'SurveyAnalyticsPage');
const SurveyResponsesPage = lazyPage(() => import('./pages/SurveyResponsesPage'), 'SurveyResponsesPage');
const SurveyDistributionsPage = lazyPage(() => import('./pages/SurveyDistributionsPage'), 'SurveyDistributionsPage');
const SurveyDistributionDetailPage = lazyPage(() => import('./pages/SurveyDistributionDetailPage'), 'SurveyDistributionDetailPage');
const AiDesignerPage = lazyPage(() => import('./pages/AiDesignerPage'), 'AiDesignerPage');
const WorkspaceSettingsPage = lazyPage(() => import('./pages/WorkspaceSettingsPage'), 'WorkspaceSettingsPage');
const ApiKeysPage = lazyPage(() => import('./pages/ApiKeysPage'), 'ApiKeysPage');
const WebhooksPage = lazyPage(() => import('./pages/WebhooksPage'), 'WebhooksPage');
const TriggersPage = lazyPage(() => import('./pages/TriggersPage'), 'TriggersPage');
const TemplatesPage = lazyPage(() => import('./pages/TemplatesPage'), 'TemplatesPage');
const ProductIntelligencePage = lazyPage(() => import('./pages/ProductIntelligencePage'), 'ProductIntelligencePage');
const AiUsagePage = lazyPage(() => import('./pages/AiUsagePage'), 'AiUsagePage');
const EmbeddedAdminPage = lazyPage(() => import('./pages/EmbeddedAdminPage'), 'EmbeddedAdminPage');
const EmbeddedSurveysPage = lazyPage(() => import('./pages/EmbeddedSurveysPage'), 'EmbeddedSurveysPage');
const EmbedIntegrationPage = lazyPage(() => import('./pages/EmbedIntegrationPage'), 'EmbedIntegrationPage');
const UserOnboardingPage = lazyPage(() => import('./pages/UserOnboardingPage'), 'UserOnboardingPage');
const ConnectedAppsPage = lazyPage(() => import('./pages/ConnectedAppsPage'), 'ConnectedAppsPage');
const SystemUsersPage = lazyPage(() => import('./pages/SystemUsersPage'), 'SystemUsersPage');
const SystemSurveysPage = lazyPage(() => import('./pages/SystemSurveysPage'), 'SystemSurveysPage');
const EmbedPreviewPage = lazyPage(() => import('./pages/EmbedPreviewPage'), 'EmbedPreviewPage');

function SurveyRendererRedirect({ results = false }: { results?: boolean }) {
  const { workspaceSlug, slug } = useParams<{ workspaceSlug?: string; slug: string }>();

  React.useEffect(() => {
    if (!slug) return;
    const target = results ? getSurveyResultsUrl(slug, workspaceSlug) : getSurveyShareUrl(slug, workspaceSlug);
    window.location.replace(`${target}${window.location.search}`);
  }, [results, slug, workspaceSlug]);

  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight={placeholderMinHeight()}>
      <CircularProgress color="inherit" sx={{ color: 'text.secondary' }} />
    </Box>
  );
}

// Feedback operations was split up; keep old links (activation checklist, OAuth returns) working.
function LegacyOperationsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get('tab');
  params.delete('tab');
  let path = '/connected-apps';
  if (tab === 'inbox' || tab === 'delivery') {
    path = '/intelligence';
    params.set('tab', 'inbox');
  } else if (tab === 'governance') {
    path = '/settings';
    params.set('tab', 'security');
  }
  const search = params.toString();
  return <Navigate to={search ? `${path}?${search}` : path} replace />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isEmbedded, embedContext, embedError } = useAuth();
  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={placeholderMinHeight(isEmbedded || isInFrame())} sx={{ color: 'text.secondary' }}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }
  if (!user && isEmbedded) {
    const theme = embedContext?.theme;
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={placeholderMinHeight(true)}
        px={2}
        sx={{
          bgcolor: theme?.backgroundColor ?? 'background.default',
          color: theme?.textColor ?? 'text.primary',
          fontFamily: theme?.fontFamily ?? undefined,
        }}
      >
        <Box maxWidth={420} textAlign="center">
          {theme?.logoUrl && (
            <Box component="img" src={theme.logoUrl} alt="" sx={{ maxHeight: 48, maxWidth: 180, objectFit: 'contain', mb: 2 }} />
          )}
          <Typography variant="h6" fontWeight={700} mb={1}>Embedded session unavailable</Typography>
          <Typography variant="body2" color="text.secondary">
            {embedError ?? 'Unable to start this embedded workspace.'}
          </Typography>
        </Box>
      </Box>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const isSignup = location.pathname === '/register';
  React.useEffect(() => {
    if (!user) return;
    // A trial link never asks for a paid tier (requestedBillingTier → null clears any pending one).
    // The signup page starts every plan link with the free trial and opens checkout itself when
    // the visitor opts to pay now, so it never leaves a tier for the layout to resume.
    storePendingBillingTier(isSignup ? null : requestedBillingTier(location.search));
  }, [isSignup, location.search, user]);
  if (user) return <Navigate to={isTrialSignup(location.search) || isSignup ? '/getting-started' : '/dashboard'} replace />;
  return <>{children}</>;
}

function EmbeddedCapabilityRoute({ capability, children }: { capability: EmbedCapability; children: React.ReactNode }) {
  const { isEmbedded, embedContext } = useAuthStore();
  if (!isEmbedded) return <>{children}</>;
  const legacyAdmin = embedContext?.role === 'admin' && !embedContext.capabilities;
  if (!legacyAdmin && !embedContext?.capabilities?.includes(capability)) {
    return <Navigate to="/embedded/admin" replace />;
  }
  return <>{children}</>;
}

/** Pages for the workspace's own team only: an embedded session belongs to a host product's users. */
function NotEmbeddedRoute({ children }: { children: React.ReactNode }) {
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  if (isEmbedded) return <Navigate to="/embedded/admin" replace />;
  return <>{children}</>;
}

export default function App() {
  const location = useLocation();
  return (
    <AppErrorBoundary resetKey={location.pathname}>
    <React.Suspense fallback={<RouteLoading />}>
    <Routes>
      <Route path="/s/:slug" element={<SurveyRendererRedirect />} />
      <Route path="/s/:slug/results" element={<SurveyRendererRedirect results />} />
      <Route path="/w/:workspaceSlug/s/:slug" element={<SurveyRendererRedirect />} />
      <Route path="/w/:workspaceSlug/s/:slug/results" element={<SurveyRendererRedirect results />} />
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/register" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/reset-password" element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />
      <Route path="/remove-account" element={<RemoveAccountPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="getting-started" element={<UserOnboardingPage />} />
        <Route path="operations" element={<LegacyOperationsRedirect />} />
        <Route path="connected-apps" element={<ConnectedAppsPage />} />
        <Route path="embedded/admin" element={<EmbeddedAdminPage />} />
        <Route path="embedded/surveys" element={<EmbeddedSurveysPage />} />
        <Route path="intelligence" element={<ProductIntelligencePage />} />
        <Route path="surveys" element={<SurveysPage />} />
        <Route path="surveys/new/ai" element={<AiDesignerPage />} />
        <Route path="ai-usage" element={<AiUsagePage />} />
        <Route path="surveys/:surveyId/edit" element={<EmbeddedCapabilityRoute capability="questionnaire:edit"><SurveyBuilderPage /></EmbeddedCapabilityRoute>} />
        <Route path="surveys/:surveyId/analytics" element={<EmbeddedCapabilityRoute capability="results:view"><SurveyAnalyticsPage /></EmbeddedCapabilityRoute>} />
        <Route path="surveys/:surveyId/responses" element={<EmbeddedCapabilityRoute capability="results:view"><SurveyResponsesPage /></EmbeddedCapabilityRoute>} />
        <Route path="surveys/:surveyId/distributions" element={<NotEmbeddedRoute><SurveyDistributionsPage /></NotEmbeddedRoute>} />
        <Route path="surveys/:surveyId/distributions/:distributionId" element={<NotEmbeddedRoute><SurveyDistributionDetailPage /></NotEmbeddedRoute>} />
        <Route path="templates" element={<EmbeddedCapabilityRoute capability="questionnaire:create"><TemplatesPage /></EmbeddedCapabilityRoute>} />
        <Route path="embed-preview" element={<EmbedPreviewPage />} />
        <Route path="settings" element={<WorkspaceSettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="embed-integration" element={<EmbedIntegrationPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="triggers" element={<TriggersPage />} />
        <Route path="system/users" element={<SystemUsersPage />} />
        <Route path="system/surveys" element={<SystemSurveysPage />} />
      </Route>
    </Routes>
    </React.Suspense>
    </AppErrorBoundary>
  );
}

function RouteLoading() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight={placeholderMinHeight()} sx={{ color: 'text.secondary' }}>
      <CircularProgress color="inherit" aria-label="Loading page" />
    </Box>
  );
}
